import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "services/api.service";

type Policy = { enrolled: boolean; legalHoldDurationDays: number; enrollmentAvailable: boolean };

export default function QuarantinePolicyForm({ clientUuid }: { clientUuid: string }) {
  const [saved, setSaved] = useState<Policy>();
  const [enrolled, setEnrolled] = useState(false);
  const [days, setDays] = useState("30");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const endpoint = `/clients/${clientUuid}/quarantine_policy`;

  useEffect(() => {
    let active = true;
    setSaved(undefined);
    setError("");
    api
      .get<Policy>(endpoint)
      .then(({ data }) => {
        if (!active) return;
        setSaved(data);
        setEnrolled(data.enrolled);
        setDays(String(data.legalHoldDurationDays));
      })
      .catch(() => {
        if (active) setError("Could not load quarantine settings. Please reload to try again.");
      });
    return () => {
      active = false;
    };
  }, [endpoint]);

  const validDays = /^\d+$/.test(days) && Number(days) > 0 && Number(days) <= 2147483647;
  const save = async () => {
    setConfirming(false);
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      const { data } = await api.put<Policy>(endpoint, {
        quarantinePolicy: { enrolled, legalHoldDurationDays: Number(days) },
      });
      setSaved(data);
      setEnrolled(data.enrolled);
      setDays(String(data.legalHoldDurationDays));
      setSuccess(true);
    } catch {
      setError("Could not save quarantine settings. Check the duration and enrollment availability, then try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!saved) return error ? <Alert severity="error">{error}</Alert> : <LinearProgress />;

  return (
    <Box
      component="form"
      sx={{ p: 4, my: 4, bgcolor: "background.paper", borderRadius: 1 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!validDays || saving) return;
        if (saved.enrolled && !enrolled) setConfirming(true);
        else void save();
      }}
    >
      <Typography fontWeight={600} fontSize={20} mb={2}>
        Quarantine
      </Typography>
      {!saved.enrollmentAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enrollment is unavailable until inbound enforcement is ready.
        </Alert>
      )}
      <FormControlLabel
        label="Quarantine new DTR orders"
        control={
          <Checkbox
            checked={enrolled}
            disabled={saving || (!saved.enrollmentAvailable && !saved.enrolled)}
            onChange={(_, checked) => {
              setEnrolled(checked);
              setSuccess(false);
            }}
          />
        }
      />
      <TextField
        label="Hold duration (days)"
        value={days}
        type="number"
        fullWidth
        sx={{ my: 2 }}
        disabled={saving}
        inputProps={{ min: 1, max: 2147483647, step: 1 }}
        onChange={(event) => {
          setDays(event.target.value);
          setSuccess(false);
        }}
        error={!validDays}
        helperText={
          !validDays ? "Enter a positive whole number of days." : "The hold starts when each device is received."
        }
      />
      <Alert severity="warning">
        Changes apply to future DTR orders only. Existing orders retain their enrollment and duration; devices already
        on hold keep their dates.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Quarantine settings saved.
        </Alert>
      )}
      <Button type="submit" variant="contained" disabled={!validDays || saving} sx={{ mt: 2 }}>
        Save quarantine settings
      </Button>
      <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="disable-quarantine-title">
        <DialogTitle id="disable-quarantine-title">Turn off quarantine?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            New DTR orders will no longer be held. Existing orders retain their quarantine policy, including orders
            still in transit.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button color="warning" onClick={() => void save()}>
            Turn off quarantine
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
